import { useState } from 'react';

import { Badge } from '@/components/ui/catalyst/badge';
import { Button } from '@/components/ui/catalyst/button';
import { Checkbox, CheckboxField } from '@/components/ui/catalyst/checkbox';
import {
  Description,
  Field,
  FieldGroup,
  Fieldset,
  Label,
} from '@/components/ui/catalyst/fieldset';
import { Input } from '@/components/ui/catalyst/input';
import { Select } from '@/components/ui/catalyst/select';
import { Textarea } from '@/components/ui/catalyst/textarea';

import { JURISDICTION_TYPES } from '../../utils/constants';
import { DepartmentFormData } from './types';

interface DepartmentSidebarEditProps {
  formData: DepartmentFormData;
  onFormDataChange: (data: DepartmentFormData) => void;
  onSave: () => Promise<boolean>;
  onCancel: () => void;
  isLoading: boolean;
}

export const DepartmentSidebarEdit = ({
  formData,
  onFormDataChange,
  onSave,
  onCancel,
  isLoading,
}: DepartmentSidebarEditProps) => {
  const [aliasInput, setAliasInput] = useState('');

  const handleAddAlias = () => {
    const trimmed = aliasInput.trim();
    if (trimmed && !formData.aliases.includes(trimmed)) {
      onFormDataChange({
        ...formData,
        aliases: [...formData.aliases, trimmed],
      });
      setAliasInput('');
    }
  };

  const handleRemoveAlias = (aliasToRemove: string) => {
    onFormDataChange({
      ...formData,
      aliases: formData.aliases.filter((alias) => alias !== aliasToRemove),
    });
  };

  const handleSaveClick = async () => {
    const success = await onSave();
    if (success) {
      // Success handling is done in the hook
    }
  };

  return (
    <div className="rounded-lg border bg-white p-4 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-900">Edit Department</h2>
      </div>

      <Fieldset>
        <FieldGroup>
          <Field>
            <Label>Name</Label>
            <Input
              value={formData.name}
              onChange={(e) =>
                onFormDataChange({ ...formData, name: e.target.value })
              }
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field>
              <Label>State</Label>
              <Input
                value={formData.stateCode}
                onChange={(e) =>
                  onFormDataChange({
                    ...formData,
                    stateCode: e.target.value.toUpperCase(),
                  })
                }
                placeholder="CA"
                maxLength={2}
              />
            </Field>
            <Field>
              <Label>Jurisdiction</Label>
              <Select
                value={formData.jurisdictionType}
                onChange={(e) =>
                  onFormDataChange({
                    ...formData,
                    jurisdictionType: e.target.value,
                  })
                }
              >
                <option value="">Select</option>
                {JURISDICTION_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </Select>
            </Field>
          </div>

          <Field>
            <Label>Aliases</Label>
            {formData.aliases.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-1.5">
                {formData.aliases.map((alias) => (
                  <Badge key={alias} color="zinc" className="text-xs">
                    {alias}
                    <button
                      type="button"
                      onClick={() => handleRemoveAlias(alias)}
                      className="ml-1 hover:text-red-600"
                    >
                      ×
                    </button>
                  </Badge>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <Input
                value={aliasInput}
                onChange={(e) => setAliasInput(e.target.value)}
                placeholder="Add alias"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddAlias();
                  }
                }}
              />
              <Button outline onClick={handleAddAlias}>
                Add
              </Button>
            </div>
          </Field>

          <Field>
            <Label>Description</Label>
            <Textarea
              value={formData.description}
              onChange={(e) =>
                onFormDataChange({ ...formData, description: e.target.value })
              }
              rows={3}
            />
          </Field>

          <CheckboxField>
            <Checkbox
              checked={formData.doNotCall}
              onChange={(checked) =>
                onFormDataChange({ ...formData, doNotCall: checked })
              }
            />
            <Label>Do not call</Label>
            <Description>Prevents voice agent outreach</Description>
          </CheckboxField>
        </FieldGroup>
      </Fieldset>

      <div className="mt-4 flex gap-2">
        <Button plain onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>
        <Button onClick={handleSaveClick} disabled={isLoading}>
          Save
        </Button>
      </div>
    </div>
  );
};
